import {joiResolver} from '@hookform/resolvers/joi';
import React, {useEffect, useMemo} from 'react';
import {useForm, UseFormReturn} from 'react-hook-form';
import {useHistory, useLocation} from 'react-router-dom';
import {WorkPassType} from '../../../api-refactored/candidate/useGetWorkPassTypes';
import {
  usePostSubmitParentParticulars
} from '../../../api-refactored/candidate/usePutSubmitParentParticular';
import {
  ParentDetailTypesResponse
} from '../../../api-refactored/non-national-exam-registration/useGetParentDetailTypes';
import {
  UserCitizenShipsResponse
} from '../../../api-refactored/non-national-exam-registration/useGetUserCitizenShips';
import {ParentParticularDetails} from '../../../api/candidate/exam/model';
import {ParentParticularFormDefaultValues} from '../../../models';
import {
  ParentParticularsLocation
} from '../../../pages/examRegistration/NonNationalExam/AEIS/ParentParticulars';
import RouteKey from '../../../routes/key';
import {docType} from '../components/nonNationalExam/ParentParticularsAeisExam';
import particularSchema from '../schema/parentParticularsExam';

interface ParticularNonNationalProps {
  parentDetails: ParentDetailTypesResponse[] | undefined;
  workPassTypes: WorkPassType[] | undefined;
  citizenShips: UserCitizenShipsResponse[] | undefined;
  applicationUuid: string;
  parentParticularDetails: ParentParticularDetails | undefined;
  setErrorMsg: React.Dispatch<React.SetStateAction<string>>;
}

interface ParticularContinueReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */
  submitContinue: (submitData: any) => void;
  /**
   * Form to send edit & delete
   */
  form: UseFormReturn<Record<string, any>>;

  formDefaultValues: ParentParticularFormDefaultValues | undefined;
  /**
   * React query mutation object
   */

  status: any;

  nextPage: () => void;
}

/**
 * A hook to handle search functionality
 * @returns
 */

const {examSchoolingDetails} = RouteKey.exam.child;

export const useParticularNonNationalAeis = ({
                                               applicationUuid,
                                               parentParticularDetails,
                                               citizenShips,
                                               parentDetails,
                                               workPassTypes,
                                               setErrorMsg,
                                             }: ParticularNonNationalProps): ParticularContinueReturn => {
  const history = useHistory();
  const {state} = useLocation<ParentParticularsLocation>();

  const formDefaultValues: any | undefined = useMemo(() => {
    if (!parentParticularDetails) {
      return undefined;
    }

    const parentTypeDefault = parentDetails?.find(
        (v) => v.description === parentParticularDetails.parentType
    );

    const citizenshipFather =
        citizenShips?.find(
            (v) => v.description === parentParticularDetails.fatherDetail?.citizenship
        ) || undefined;
    const documentTypeFather =
        docType?.find((v) => v.code === parentParticularDetails.fatherDetail?.documentType) ||
        undefined;
    const workPassTypeFather =
        workPassTypes?.find((v) => v.code === parentParticularDetails.fatherDetail?.workPassType) ||
        undefined;

    const citizenshipMother =
        citizenShips?.find(
            (v) => v.description === parentParticularDetails.motherDetail?.citizenship
        ) || undefined;
    const documentTypeMother =
        docType?.find((v) => v.code === parentParticularDetails.motherDetail?.documentType) ||
        undefined;
    const workPassTypeMother =
        workPassTypes?.find((v) => v.code === parentParticularDetails.motherDetail?.workPassType) ||
        undefined;

    return {
      name: parentParticularDetails.declaringPerson.name || '',
      uinFinPassport: parentParticularDetails.declaringPerson?.uinFinPassport || '',
      parentType: parentTypeDefault,
      fatherName: parentParticularDetails.fatherDetail?.name,
      fatherCitizen: citizenshipFather,
      fatherDocType: documentTypeFather,
      fatherDocNo: parentParticularDetails.fatherDetail?.documentNumber || '',
      fatherWorkPassType: workPassTypeFather,
      fatherFin: parentParticularDetails.fatherDetail?.fin || '',
      motherName: parentParticularDetails.motherDetail?.name || '',
      motherCitizen: citizenshipMother,
      motherDocType: documentTypeMother,
      motherDocNo: parentParticularDetails.motherDetail?.documentNumber || '',
      motherWorkPassType: workPassTypeMother,
      motherFin: parentParticularDetails.motherDetail?.fin || '',
    };
  }, [citizenShips, parentDetails, parentParticularDetails, workPassTypes]);

  const form = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(particularSchema),
    defaultValues: formDefaultValues,
  });

  useEffect(
      () => {
        if (formDefaultValues) {
          form.reset(formDefaultValues);
        }
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [formDefaultValues]
  );

  const submitContinue = (submitData: any) => {
    if (parentParticularDetails?.lockedByUser) {
      nextPage();
      return;
    }
    const data: any = {
      parentType: submitData?.parentType?.code,
      declaringPerson: {
        name: submitData?.name,
        uinFinPassport: submitData?.uinFinPassport,
      },
    };
    if (
        submitData?.parentType?.code === 'Father' ||
        submitData?.parentType?.code === 'Father and Mother'
    ) {
      data.fatherDetail = {
        name: submitData?.fatherName,
        citizenship: submitData?.fatherCitizen?.code,
        documentType: submitData?.fatherDocType?.code,
        documentNumber: submitData?.fatherDocNo || null,
        workPassType: submitData?.fatherWorkPassType?.code || null,
        fin: submitData?.fatherFin || null,
      };
    } else {
      data.fatherDetail = null;
    }
    if (
        submitData?.parentType?.code === 'Mother' ||
        submitData?.parentType?.code === 'Father and Mother'
    ) {
      data.motherDetail = {
        name: submitData?.motherName,
        citizenship: submitData?.motherCitizen?.code,
        documentType: submitData?.motherDocType?.code,
        documentNumber: submitData?.motherDocNo || null,
        workPassType: submitData?.motherWorkPassType?.code || null,
        fin: submitData?.motherFin || null,
      };
    } else {
      data.motherDetail = null;
    }
    submitParentParticularMutation.mutate(data);
  };

  const submitParentParticularMutation = usePostSubmitParentParticulars({
    uuid: applicationUuid,
    onSuccess: () => nextPage(),
    onError: (err) => setErrorMsg(err?.response?.data.message!),
  });

  const nextPage = () => {
    history.push({
      pathname: `${examSchoolingDetails.route}`,
      state: {
        examType: state?.examType,
        examTitle: state?.examTitle,
        levelSeekingAdmission: state?.levelSeekingAdmission,
        isSupplementary: state?.isSupplementary,
      },
    });
  };

  return {
    form,
    formDefaultValues,
    submitContinue,
    status: submitParentParticularMutation.status,
    nextPage,
  };
};
